/**
 * What an expense was *for* — the short list the sheet's category picker
 * offers, and the only place that list exists.
 *
 * It is one module imported by both halves, the way `src/lib/money.ts` is, so
 * the picker and the validator cannot drift: a category the form can offer is
 * exactly a category the API will accept, and adding one is a line here rather
 * than a line here and a `CHECK` in a migration and a constant in a handler.
 *
 * **The column is deliberately not a `CHECK`.** `transactions.category` is
 * plain TEXT with only a length check, because SQLite cannot widen a `CHECK`
 * without rebuilding the table (migration 0010 is what that costs), and a new
 * category is a product decision that should not cost a table rebuild. The
 * API refuses one it does not know, so the database never holds a category
 * this app cannot name — it is just not the database doing the refusing.
 *
 * A category is optional. "Uncategorised" is `null`, not a category called
 * "other" that quietly becomes the biggest bucket on any screen that ever
 * charts these.
 */

export type Category = {
  id: string
  label: string
  /** A second, non-colour cue for the same fact — see `src/index.css`. */
  glyph: string
}

export const CATEGORIES: readonly Category[] = [
  { id: 'groceries', label: 'Groceries', glyph: '🛒' },
  { id: 'dining', label: 'Food & drink', glyph: '🍽️' },
  { id: 'accommodation', label: 'Accommodation', glyph: '🏠' },
  { id: 'transport', label: 'Transport', glyph: '🚗' },
  { id: 'entertainment', label: 'Entertainment', glyph: '🎟️' },
  { id: 'utilities', label: 'Utilities', glyph: '💡' },
  { id: 'shopping', label: 'Shopping', glyph: '🛍️' },
  { id: 'health', label: 'Health', glyph: '🩺' },
  { id: 'general', label: 'General', glyph: '🧾' },
]

export const CATEGORY_IDS: readonly string[] = CATEGORIES.map((category) => category.id)

export function isCategory(value: unknown): value is string {
  return typeof value === 'string' && CATEGORY_IDS.includes(value)
}

export function categoryFor(id: string | null): Category | null {
  if (id === null) return null
  return CATEGORIES.find((category) => category.id === id) ?? null
}

/**
 * A category id as a person reads it. An id this build does not know is shown
 * as itself rather than dropped — a row written by a newer deploy is still a
 * row, and a screen that silently hid it would be lying about what is stored.
 */
export function categoryLabel(id: string | null): string {
  if (id === null) return 'Uncategorised'
  return categoryFor(id)?.label ?? id
}
